"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Duration = void 0;
exports.durationOf = durationOf;
const misc_1 = require("@moojo/misc");
/**
 * An immutable length of time, in milliseconds granularity
 */
// #TD(imaman): zod support
class Duration {
    amountMillis;
    constructor(amountMillis) {
        this.amountMillis = amountMillis;
        if (!Number.isFinite(amountMillis)) {
            throw new Error(`Not a finite number (${amountMillis})`);
        }
        if (!Number.isSafeInteger(amountMillis)) {
            throw new Error(`Not a (safe) int (${amountMillis})`);
        }
    }
    exactUnrounded(targetUnit) {
        return this.amountMillis / ratioToMillis(targetUnit);
    }
    exact(targetUnit, precision = 10) {
        const factor = Math.pow(10, precision);
        const rounded = Math.round(this.exactUnrounded(targetUnit) * factor);
        return rounded / factor;
    }
    abs() {
        return this.isNegative() ? durationOf(0).minus(this) : this;
    }
    whole(targetUnit) {
        const n = Math.floor(this.abs().exactUnrounded(targetUnit));
        return this.isNegative() ? -n : n;
    }
    sign() {
        return this.isNegative() ? '-' : '';
    }
    toString() {
        return this.format();
    }
    isZero() {
        return this.amountMillis === 0;
    }
    isNegative() {
        return this.amountMillis < 0;
    }
    negate() {
        return new Duration(-this.amountMillis);
    }
    format() {
        if (this.isZero()) {
            return `0${(0, misc_1.switchOn)('millis', letters)}`;
        }
        // TODO(imaman): can we compute it hand-free (to avoid manually updating it when a new unit is added)
        const units = ['days', 'hours', 'minutes', 'seconds', 'millis'];
        const arr = [];
        let d = this.abs();
        for (const u of units) {
            const n = d.whole(u);
            if (n > 0) {
                arr.push(`${n}${(0, misc_1.switchOn)(u, letters)}`);
            }
            d = d.minus(durationOf(n, u));
        }
        return `${this.sign()}${arr.join('')}`;
    }
    plus(rhs) {
        return new Duration(this.amountMillis + rhs.amountMillis);
    }
    minus(rhs) {
        return new Duration(this.amountMillis - rhs.amountMillis);
    }
    compare(that) {
        const diff = this.minus(that);
        return diff.isZero() ? 0 : diff.isNegative() ? -1 : 1;
    }
    is(operator, other) {
        const c = this.compare(other);
        return (0, misc_1.switchOn)(operator, {
            '<': () => c < 0,
            '<=': () => c <= 0,
            '==': () => c == 0,
            '>=': () => c >= 0,
            '>': () => c > 0,
        });
    }
}
exports.Duration = Duration;
const letters = {
    millis: () => 'ms',
    seconds: () => 's',
    minutes: () => 'm',
    hours: () => 'h',
    days: () => 'd',
};
function ratioToMillis(unit) {
    if (unit === 'millis') {
        return 1;
    }
    if (unit === 'seconds') {
        return 1000;
    }
    if (unit === 'minutes') {
        return 1000 * 60;
    }
    if (unit === 'hours') {
        return 1000 * 60 * 60;
    }
    if (unit === 'days') {
        return 1000 * 60 * 60 * 24;
    }
    (0, misc_1.shouldNeverHappen)(unit);
}
function durationOf(...args) {
    if (args.length === 1) {
        return new Duration(args[0]);
    }
    if (args.length === 2) {
        const [amount, unit] = args;
        return new Duration(ratioToMillis(unit) * amount);
    }
    (0, misc_1.shouldNeverHappen)(args);
}
//# sourceMappingURL=data:application/json;base64,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